// src/Dashboard/pages/user/DashboardUser/components/ObjectifsPanel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaBullseye, FaCheck, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";
import {
  createObjectif,
  deleteObjectif,
  getObjectifsByAdherent,
  setObjectifDone,
  setObjectifProgress,
  updateObjectif,
} from "../../../../../services/objectifsServices";

import EmptyState from "./EmptyState";
import ObjectifDrawer from "./ObjectifDrawer";

function ObjectifCard({
  objectif,
  onEdit,
  onDelete,
  onToggleDone,
  onProgressChange,
}) {
  const progress = Math.max(0, Math.min(100, Number(objectif.progress || 0)));

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-bold text-slate-900 dark:text-white">
              {objectif.title}
            </h3>

            {objectif.saison && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {objectif.saison}
              </span>
            )}

            {objectif.type && (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                {objectif.type}
              </span>
            )}

            <span
              className={[
                "rounded-full px-2.5 py-1 text-xs font-bold",
                objectif.is_done
                  ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200"
                  : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200",
              ].join(" ")}
            >
              {objectif.is_done ? "Terminé" : "En cours"}
            </span>
          </div>

          {objectif.description && (
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {objectif.description}
            </p>
          )}
        </div>

        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => onToggleDone(objectif)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-200"
            title={
              objectif.is_done
                ? "Marquer comme non terminé"
                : "Marquer comme terminé"
            }
          >
            <FaCheck />
          </button>

          <button
            type="button"
            onClick={() => onEdit(objectif)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200"
            title="Modifier"
          >
            <FaEdit />
          </button>

          <button
            type="button"
            onClick={() => onDelete(objectif)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-200"
            title="Supprimer"
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="mt-4">
        <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-violet-500 to-orange-400"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="mt-2 flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={100}
            value={progress}
            disabled={objectif.is_done}
            onChange={(event) => onProgressChange(objectif, event.target.value)}
            className="w-full disabled:opacity-50"
          />

          <span className="w-12 text-right text-xs font-bold text-slate-500 dark:text-slate-400">
            {progress}%
          </span>
        </div>
      </div>
    </article>
  );
}

export default function ObjectifsPanel({ userId, onCounts }) {
  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [objectifs, setObjectifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingObjectif, setEditingObjectif] = useState(null);

  const prevCountRef = useRef(-1);

  useEffect(() => {
    let cancelled = false;

    const loadAdherents = async () => {
      if (!userId) return;

      try {
        const list = await getAdherentsByUser(userId);
        if (cancelled) return;

        const sorted = [...(Array.isArray(list) ? list : [])].sort(
          (a, b) =>
            String(a.nom || "").localeCompare(String(b.nom || ""), "fr", {
              sensitivity: "base",
            }) ||
            String(a.prenom || "").localeCompare(String(b.prenom || ""), "fr", {
              sensitivity: "base",
            })
        );

        setAdherents(sorted);

        if (sorted.length > 0) {
          setSelectedAdherentId(String(sorted[0].id));
        }
      } catch (error) {
        console.error("[ObjectifsPanel] load adherents error:", error);
        toast.error("Impossible de charger les adhérents.");
      }
    };

    loadAdherents();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const selectedAdherent = useMemo(
    () =>
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId)
      ) || null,
    [adherents, selectedAdherentId]
  );

  const loadObjectifs = useCallback(async () => {
    if (!selectedAdherentId) {
      setObjectifs([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getObjectifsByAdherent(selectedAdherentId);
      setObjectifs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[ObjectifsPanel] load objectifs error:", error);
      toast.error("Impossible de charger les objectifs.");
    } finally {
      setLoading(false);
    }
  }, [selectedAdherentId]);

  useEffect(() => {
    loadObjectifs();
  }, [loadObjectifs]);

  useEffect(() => {
    if (!onCounts) return;

    const activeCount = objectifs.filter((objectif) => !objectif.is_done).length;

    if (prevCountRef.current === activeCount) return;

    prevCountRef.current = activeCount;
    onCounts({ total: activeCount });
  }, [objectifs, onCounts]);

  const openCreate = () => {
    if (!selectedAdherentId) {
      toast.info("Sélectionnez d’abord un adhérent.");
      return;
    }

    setEditingObjectif(null);
    setDrawerOpen(true);
  };

  const openEdit = (objectif) => {
    setEditingObjectif(objectif);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload, idToUpdate) => {
    if (idToUpdate) {
      await updateObjectif(idToUpdate, payload);
      toast.success("Objectif mis à jour.");
    } else {
      await createObjectif(payload);
      toast.success("Objectif créé.");
    }

    await loadObjectifs();
  };

  const handleDelete = async (objectif) => {
    if (!window.confirm(`Supprimer l’objectif « ${objectif.title} » ?`)) return;

    try {
      await deleteObjectif(objectif.id);
      toast.success("Objectif supprimé.");
      await loadObjectifs();
    } catch (error) {
      console.error("[ObjectifsPanel] delete error:", error);
      toast.error("Impossible de supprimer cet objectif.");
    }
  };

  const handleToggleDone = async (objectif) => {
    try {
      await setObjectifDone(objectif.id, !objectif.is_done);
      await loadObjectifs();
    } catch (error) {
      console.error("[ObjectifsPanel] done error:", error);
      toast.error("Impossible de modifier l’état de l’objectif.");
    }
  };

  const handleProgressChange = async (objectif, value) => {
    const progress = Math.max(0, Math.min(100, Number(value) || 0));

    setObjectifs((current) =>
      current.map((item) =>
        item.id === objectif.id
          ? {
              ...item,
              progress,
              is_done: progress >= 100 ? true : item.is_done,
            }
          : item
      )
    );

    try {
      await setObjectifProgress(objectif.id, progress);
    } catch (error) {
      console.error("[ObjectifsPanel] progress error:", error);
      toast.error("Impossible de modifier la progression.");
      await loadObjectifs();
    }
  };

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-sm">
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Adhérent
          </label>
          <select
            value={selectedAdherentId}
            onChange={(event) => setSelectedAdherentId(event.target.value)}
            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          >
            <option value="">— Sélectionner —</option>
            {adherents.map((adherent) => (
              <option key={adherent.id} value={adherent.id}>
                {String(adherent.nom || "").toUpperCase()} {adherent.prenom}
              </option>
            ))}
          </select>

          {selectedAdherent && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Objectifs de {selectedAdherent.prenom} {selectedAdherent.nom}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
        >
          <FaPlus />
          Nouvel objectif
        </button>
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="h-36 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      ) : objectifs.length === 0 ? (
        <EmptyState
          icon={FaBullseye}
          title="Aucun objectif"
          description="Ajoutez un objectif de saison pour suivre la progression d’un adhérent."
          action={
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700"
            >
              <FaPlus />
              Créer un objectif
            </button>
          }
        />
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {objectifs.map((objectif) => (
            <ObjectifCard
              key={objectif.id}
              objectif={objectif}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleDone={handleToggleDone}
              onProgressChange={handleProgressChange}
            />
          ))}
        </div>
      )}

      <ObjectifDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingObjectif(null);
        }}
        initial={editingObjectif}
        adherentId={Number(selectedAdherentId)}
        onSubmit={handleSubmit}
      />
    </>
  );
}